import { type Dispatch, type MutableRefObject, type SetStateAction, useCallback } from 'react'
import { FIT_TO_SPREAD_VIEW_PADDING_PX } from '../constants/canvas-zoom'
import { clampScrollerToRange } from '../helpers/clamp-scroll'
import { computeFitSpreadScale } from '../helpers/zoom-scale'

/**
 * Fit-to-spread and actual-size viewport commands: fit the scale to the document, then align or
 * center the document in the scroller. Effects that drive these live in `use-fit-to-spread-effects`
 * and `use-spread-visibility`.
 */
export function useFitToSpread({
  scrollRef,
  documentCanvasRef,
  zoomRef,
  setScale,
  fitContentDimensions,
  syncRulerScrollTransforms,
}: {
  scrollRef: MutableRefObject<HTMLDivElement | null>
  documentCanvasRef: MutableRefObject<HTMLDivElement | null>
  zoomRef: MutableRefObject<number>
  setScale: Dispatch<SetStateAction<number>>
  fitContentDimensions: { width: number; height: number }
  syncRulerScrollTransforms: () => void
}) {
  /**
   * After fit scale is applied, align the document to the scroller using the same padded box as
   * the fit ratio (top + horizontal center). Avoids centering the whole pasteboard, which leaves
   * a large gap under the ruler.
   */
  const alignFitSpreadInViewport = useCallback(() => {
    const scroller = scrollRef.current
    const canvasEl = documentCanvasRef.current

    if (!scroller || !canvasEl) return

    const pad = FIT_TO_SPREAD_VIEW_PADDING_PX
    const sc = scroller.getBoundingClientRect()
    const doc = canvasEl.getBoundingClientRect()

    const innerLeft = sc.left + pad
    const innerWidth = Math.max(0, sc.width - pad * 2)
    const desiredDocLeft = innerLeft + (innerWidth - doc.width) / 2
    const desiredDocTop = sc.top + pad

    scroller.scrollLeft += doc.left - desiredDocLeft
    scroller.scrollTop += doc.top - desiredDocTop

    clampScrollerToRange(scroller, scroller.scrollLeft, scroller.scrollTop)

    syncRulerScrollTransforms()
  }, [syncRulerScrollTransforms, scrollRef, documentCanvasRef])

  /** Center document canvas in the scroller viewport (100% / “actual size” after explicit zoom). */
  const centerSpreadForActualSize = useCallback(() => {
    const scroller = scrollRef.current
    const canvasEl = documentCanvasRef.current

    if (!scroller || !canvasEl) return

    const sc = scroller.getBoundingClientRect()
    const doc = canvasEl.getBoundingClientRect()
    const docCenterX = doc.left + doc.width / 2
    const docCenterY = doc.top + doc.height / 2
    const scCenterX = sc.left + sc.width / 2
    const scCenterY = sc.top + sc.height / 2

    scroller.scrollLeft += docCenterX - scCenterX
    scroller.scrollTop += docCenterY - scCenterY

    clampScrollerToRange(scroller, scroller.scrollLeft, scroller.scrollTop)

    syncRulerScrollTransforms()
  }, [syncRulerScrollTransforms, scrollRef, documentCanvasRef])

  const recomputeFitScaleAndAlign = useCallback(() => {
    const scroller = scrollRef.current

    if (!scroller) return

    const { width: docW, height: docH } = fitContentDimensions
    const nextScale = computeFitSpreadScale(scroller.clientWidth, scroller.clientHeight, docW, docH)

    setScale(nextScale)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        alignFitSpreadInViewport()
      })
    })
  }, [alignFitSpreadInViewport, fitContentDimensions, scrollRef, setScale])

  const refitFitSpread = useCallback(() => {
    if (zoomRef.current !== 0) return

    recomputeFitScaleAndAlign()
  }, [recomputeFitScaleAndAlign, zoomRef])

  return {
    alignFitSpreadInViewport,
    centerSpreadForActualSize,
    recomputeFitScaleAndAlign,
    refitFitSpread,
  }
}
